import {httpClientV2} from "../../infrastructure/http/httpClient";
import {
  PublicProfileDocument,
  PublicProfileSurfaceStyle,
  PublicProfileThemeFont,
  PublicProfileThemeId,
  PUBLIC_PROFILE_THEME_BLOCK_COLOURS,
  PUBLIC_PROFILE_THEMES,
  resolvePublicProfileThemeSettings,
} from "../domain";

type ApiResponse<T> = {
  status: boolean;
  message: string;
  data: T;
};

type ThemeBackgroundType = "solid" | "gradient" | "image";

export type PublicProfileThemePayload = {
  preset: PublicProfileThemeId;
  background: {
    type: ThemeBackgroundType;
    colour: string;
    gradientColours: string[] | null;
    imageUrl: string | null;
  };
  typography: {
    headerFont: PublicProfileThemeFont;
    bodyFont: PublicProfileThemeFont;
    textColour: string;
  };
  button: {
    style: PublicProfileSurfaceStyle;
    colour: string;
    textColour: string;
    borderRadius: number;
  };
  block: {
    style: PublicProfileSurfaceStyle;
    colour: string;
    borderRadius: number;
    spacing: number;
  };
  highlight: {
    backgroundColour: string;
    borderColour: string;
    borderRadius: number;
  };
  surface: {
    backgroundColour: string;
    borderColour: string;
    borderRadius: number;
  };
};

export type PublicProfileThemeResponse = PublicProfileThemePayload & {
  id: number;
  userId: number;
  createdAt: string;
  updatedAt: string;
};

const TEXT_FALLBACKS: Record<PublicProfileThemeId, string> = {
  mono: "#1c1726",
  ledger: "#333333",
  frost: "#222222",
  lush: "#3d2b1f",
  midnight: "#f5f5f5",
  velvet: "#e9d4ff",
  mint: "#222222",
  terminal: "#3fb950",
  retro: "#222222",
  brutal: "#222222",
};

const ACCENT_TEXT_FALLBACKS: Record<PublicProfileThemeId, string> = {
  mono: "#ffffff",
  ledger: "#ffffff",
  frost: "#222222",
  lush: "#3d2b1f",
  midnight: "#111111",
  velvet: "#2a1b38",
  mint: "#052e25",
  terminal: "#0d1117",
  retro: "#222222",
  brutal: "#222222",
};

function toCssColour(colour: string): string {
  return colour.startsWith("#") ? colour : `#${colour}`;
}

function toApiColour(colour: string): string {
  return colour.replace(/^#/, "").toUpperCase();
}

export function publicProfileThemeToPayload(
  theme: PublicProfileDocument["theme"]
): PublicProfileThemePayload {
  const preset = PUBLIC_PROFILE_THEMES.find((item) => item.id === theme.preset)!;
  const settings = resolvePublicProfileThemeSettings(theme);
  const pageBackground = theme.overrides.pageBackground ?? preset.colours[0];
  const blockColour =
    theme.overrides.surfaceMuted ?? PUBLIC_PROFILE_THEME_BLOCK_COLOURS[theme.preset];

  return {
    preset: theme.preset,
    background: {
      type: settings.backgroundStyle,
      colour: toApiColour(pageBackground),
      gradientColours:
        settings.backgroundStyle === "gradient"
          ? [pageBackground, settings.backgroundGradientEnd].map(toApiColour)
          : null,
      imageUrl: settings.backgroundImageUrl,
    },
    typography: {
      headerFont: settings.headingFont,
      bodyFont: settings.bodyFont,
      textColour: toApiColour(theme.overrides.text ?? TEXT_FALLBACKS[theme.preset]),
    },
    button: {
      style: settings.buttonStyle,
      colour: toApiColour(theme.overrides.accent ?? preset.colours[2]),
      textColour: toApiColour(
        theme.overrides.accentText ?? ACCENT_TEXT_FALLBACKS[theme.preset]
      ),
      borderRadius: settings.buttonRadius,
    },
    block: {
      style: settings.blockStyle,
      colour: toApiColour(blockColour),
      borderRadius: settings.blockRadius,
      spacing: settings.blockSpacing,
    },
    highlight: {
      backgroundColour: toApiColour(settings.highlightBackground),
      borderColour: toApiColour(settings.highlightBorderColor),
      borderRadius: settings.highlightRadius,
    },
    surface: {
      backgroundColour: toApiColour(
        theme.overrides.surface ?? settings.surfaceBackground
      ),
      borderColour: toApiColour(settings.surfaceBorderColor),
      borderRadius: settings.surfaceRadius,
    },
  };
}

export function mergePublicProfileTheme(
  current: PublicProfileDocument["theme"],
  theme: PublicProfileThemePayload
): PublicProfileDocument["theme"] {
  const currentSettings = resolvePublicProfileThemeSettings(current);
  const gradientColours = theme.background.gradientColours;
  const gradientEnd = gradientColours?.[gradientColours.length - 1];

  return {
    preset: theme.preset,
    settings: {
      ...currentSettings,
      backgroundStyle: theme.background.type,
      backgroundGradientEnd: gradientEnd
        ? toCssColour(gradientEnd)
        : currentSettings.backgroundGradientEnd,
      backgroundImageUrl: theme.background.imageUrl,
      headingFont: theme.typography.headerFont,
      bodyFont: theme.typography.bodyFont,
      buttonStyle: theme.button.style,
      buttonRadius: theme.button.borderRadius,
      blockStyle: theme.block.style,
      blockRadius: theme.block.borderRadius,
      blockSpacing: theme.block.spacing,
      highlightBackground: toCssColour(theme.highlight.backgroundColour),
      highlightBorderColor: toCssColour(theme.highlight.borderColour),
      highlightRadius: theme.highlight.borderRadius,
      surfaceBackground: toCssColour(theme.surface.backgroundColour),
      surfaceBorderColor: toCssColour(theme.surface.borderColour),
      surfaceRadius: theme.surface.borderRadius,
    },
    overrides: {
      ...current.overrides,
      pageBackground: toCssColour(theme.background.colour),
      surface: toCssColour(theme.surface.backgroundColour),
      surfaceMuted: toCssColour(theme.block.colour),
      text: toCssColour(theme.typography.textColour),
      accent: toCssColour(theme.button.colour),
      accentText: toCssColour(theme.button.textColour),
    },
  };
}

export async function getPublicProfileTheme(): Promise<PublicProfileThemeResponse | null> {
  const response = await httpClientV2.get<
    ApiResponse<PublicProfileThemeResponse | null> | PublicProfileThemeResponse
  >("profile/theme");

  if ("data" in response) return response.data ?? null;
  if ("preset" in response) return response;
  return null;
}

export async function savePublicProfileTheme(
  theme: PublicProfileDocument["theme"]
): Promise<PublicProfileDocument["theme"]> {
  const response = await httpClientV2.put<ApiResponse<PublicProfileThemeResponse>>(
    "profile/theme",
    publicProfileThemeToPayload(theme)
  );
  return mergePublicProfileTheme(theme, response.data);
}
